import React from 'react';
import { Button, Grid, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';
import { AreaOccupancyData, UnavailabilityData } from '../../../api';
import { intersectMasks, popcount } from '../../../utils/maskUtils';
import { blockCoversTime } from '../utils/areaBlocks';
import { AREAS_PALETTE } from '../areasPalette';
import { formatSeconds } from '../utils/areasFormat';

type AreaBlockChipsProps = {
  blocks: UnavailabilityData[];
  area: AreaOccupancyData;
  selectedSeconds: number;
  timeFormat: string;
  /** Without it the chips are read-only: no edit button, no click target. */
  onManage?: () => void;
};

/** The blocks of one area that cover the selected time, one chip each. */
export const AreaBlockChips = ({ blocks, area, selectedSeconds, timeFormat, onManage }: AreaBlockChipsProps) => {
  const { t } = useTranslation();
  return (
    <>
      {blocks
        .filter((block) => blockCoversTime(block, selectedSeconds))
        .map((block) => {
          const blockedHere = popcount(
            intersectMasks(
              (block.shared_resources_blocks?.scheduler_configs ?? []).find((entry) => entry.area_id === area.area_id)
                ?.blocked_mask ?? '',
              area.mask,
            ),
          );
          return (
            <Grid
              key={block.id}
              container
              alignItems="center"
              gap={1}
              wrap="nowrap"
              onClick={onManage}
              sx={{
                backgroundColor: 'mb.ground',
                borderRadius: '8px',
                padding: '4px 10px',
                marginBottom: 0.5,
                ...(onManage && { cursor: 'pointer', '&:hover': { backgroundColor: 'mb.lineSoft' } }),
              }}>
              <LockIcon sx={{ fontSize: '14px', color: AREAS_PALETTE.blocked }} />
              <Typography dir="ltr" sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                {block.all_day
                  ? t('calendar.areas_view.all_day')
                  : `${formatSeconds(block.start_time, timeFormat)}–${formatSeconds(block.end_time, timeFormat)}`}
              </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {t('calendar.areas_view.blocked_chip', { count: blockedHere })}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: 'text.secondary', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                {block.description}
              </Typography>
              {onManage && (
                <Button
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    onManage();
                  }}
                  sx={{ flexShrink: 0 }}>
                  {t('calendar.areas_view.edit_block')}
                </Button>
              )}
            </Grid>
          );
        })}
    </>
  );
};
